import { ProductDto, ProductFamily, ProductsDto } from "@custom-types/products"
import {
  DivisionNode,
  FamilyNode,
  OrganisationNode,
  ProductNode,
  ScenarioTreeNode,
  ScenarioTreeNodeType,
} from "@components/ScenarioTree/types"
import { TreeViewBaseItem } from "@mui/x-tree-view"

export const createNodeTree = (products: ProductsDto | undefined): OrganisationNode[] => {
  if (!products) return []

  const organisationProductMap = Object.groupBy(
    products.items,
    (product) => product.organisation_key,
  )

  return Object.entries(organisationProductMap).map(([organisationKey, organisationProducts]) => ({
    organisationKey,
    id: organisationKey,
    label: organisationKey,
    type: ScenarioTreeNodeType.ORGANISATION,
    children: createDivisionNodes(organisationProducts ?? [], organisationKey),
  }))
}

const createDivisionNodes = (products: ProductDto[], organisationKey: string): DivisionNode[] => {
  const divisionProductMap = Object.groupBy(products, (product) => product.division_key)

  return Object.entries(divisionProductMap).map(([divisionKey, divisionProducts]) => ({
    organisationKey,
    divisionKey,
    id: `${organisationKey}#${divisionKey}`,
    label: divisionKey,
    type: ScenarioTreeNodeType.DIVISION,
    children: createProductNodes(divisionProducts ?? [], organisationKey, divisionKey),
  }))
}

const createProductNodes = (
  products: ProductDto[],
  organisationKey: string,
  divisionKey: string,
): ProductNode[] => {
  return products.map((product) => ({
    organisationKey,
    divisionKey,
    productKey: product.key,
    id: `${organisationKey}#${divisionKey}#${product.key}`,
    label: product.name,
    type: ScenarioTreeNodeType.PRODUCT,
    hasFetchedFamilies: false,
    children: [], // user needs to click the node to initate loading family nodes
  }))
}

export const createFamilyNodes = (
  families: ProductFamily[],
  organisationKey: string,
  divisionKey: string,
  productKey: string,
): FamilyNode[] => {
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  return families.map((family) => {
    const familyKey = family.key.split("#").slice(-1)[0]
    return {
      organisationKey,
      divisionKey,
      productKey,
      familyKey,
      fullFamilyKey: family.key,
      id: `${organisationKey}#${divisionKey}#${productKey}#${familyKey}`,
      label: family.name,
      type: ScenarioTreeNodeType.FAMILY,
      children: [], // user needs to click the node to initate loading family nodes
    }
  })
}

export const getNodesIds = (...nodes: ScenarioTreeNode[]) => {
  const ids = nodes.map(({ id }) => id)

  nodes.forEach((node) => ids.push(...getNodesIds(...node.children)))

  return ids
}

export const findNode = (
  nodes: ScenarioTreeNode[],
  nodeId: string,
): ScenarioTreeNode | undefined => {
  const matchingNode = nodes.find(({ id }) => nodeId === id)
  if (matchingNode) return matchingNode

  const nodesChildren = nodes.reduce<ScenarioTreeNode[]>(
    (children, node) => [...children, ...node.children],
    [],
  )
  return findNode(nodesChildren, nodeId)
}

export const findNodeByKeys = (
  nodeTree: OrganisationNode[],
  keys: { organisationKey?: string; divisionKey?: string; productKey?: string; familyKey?: string },
): ScenarioTreeNode | undefined => {
  const { organisationKey, divisionKey, productKey, familyKey } = keys

  if (!organisationKey) return

  const organisationNode = nodeTree.find((node) => node.organisationKey === organisationKey)
  if (!divisionKey || !organisationNode) return organisationNode

  const divisionNode = organisationNode.children.find((node) => node.divisionKey === divisionKey)
  if (!productKey || !divisionNode) return divisionNode

  const productNode = divisionNode.children.find((node) => node.productKey === productKey)
  if (!familyKey || !productNode) return productNode

  return productNode.children.find((node) => node.familyKey === familyKey)
}

export const filterNodes = <TNode extends TreeViewBaseItem>(
  nodes: TNode[],
  filter: string,
): TNode[] => {
  if (!filter) return nodes

  const filterValue = filter.toLowerCase()
  const nodesCopy = JSON.parse(JSON.stringify(nodes)) as TNode[]

  return nodesCopy.filter((node) => {
    const labelValue = node.label.toLowerCase()
    const isMatch = labelValue.includes(filterValue)

    node.children = filterNodes(node?.children ?? [], filterValue)
    const hasMatchingChild = node.children.length > 0

    return isMatch || hasMatchingChild
  })
}

export const replaceNode = (
  nodes: ScenarioTreeNode[],
  nodeId: string,
  updatedNode: ScenarioTreeNode,
): ScenarioTreeNode[] => {
  const nodesCopy = JSON.parse(JSON.stringify(nodes)) as ScenarioTreeNode[]
  const oldNodeIndex = nodesCopy.findIndex(({ id }) => id === nodeId)

  if (oldNodeIndex !== -1) {
    nodesCopy[oldNodeIndex] = updatedNode
    return nodesCopy
  }

  return nodesCopy.map(
    ({ children, ...node }) =>
      ({
        ...node,
        children: replaceNode(children, nodeId, updatedNode),
      } as ScenarioTreeNode),
  )
}

export const getUpdatedNodeTree = (
  treeNode: OrganisationNode[],
  nodeToUpdateId: string,
  updatedNode: ScenarioTreeNode,
) => {
  const oldNode = findNode(treeNode, nodeToUpdateId)
  if (!oldNode) return treeNode

  const newNode = { ...oldNode, ...updatedNode }
  return replaceNode(treeNode, nodeToUpdateId, newNode)
}
